'use client';

import React, { useState } from 'react';
import { Copy, Pipette } from 'lucide-react';
import { css, cx } from 'styled-system/css';
import { Stack } from 'styled-system/jsx';
import { input } from 'styled-system/recipes';

import { type Color, parseColor } from '@ark-ui/react/color-picker';
import Copiable from '@shaojiang/components/Copiable';
import { paperStyle } from '@shaojiang/components/ShaojiangStyled/ShaojiangPaper';
import { Button } from '@shaojiang/components/ui/button';
import { ColorPicker as Picker } from '@shaojiang/components/ui/color-picker';
import { IconButton } from '@shaojiang/components/ui/icon-button';

// Material palette, similar to the swatches the old react-color picker offered
const SWATCHES = [
  '#f44336',
  '#e91e63',
  '#9c27b0',
  '#673ab7',
  '#3f51b5',
  '#2196f3',
  '#03a9f4',
  '#00bcd4',
  '#009688',
  '#4caf50',
  '#8bc34a',
  '#cddc39',
  '#ffeb3b',
  '#ffc107',
  '#ff9800',
  '#ff5722',
  '#795548',
  '#9e9e9e',
  '#607d8b',
  '#000000',
  '#ffffff',
  '#9155fd',
];

const checkerboard = css({
  bgImage:
    "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAMUlEQVQ4T2NkYGAQYcAP3uCTZhw1gGGYhAGBZIA/nYDCgBDAm9BGDWAAJyRCgLaBCAAgXwixzAS0pgAAAABJRU5ErkJggg==')",
});

export default function ColorPicker() {
  const [color, setColor] = useState<Color>(() => parseColor('#9155fd'));

  const hex = color.toString('hex');
  const rgba = color.toString('rgba');

  return (
    <div
      className={css({
        display: 'grid',
        gridTemplateColumns: {
          base: 'repeat(2, minmax(0, 1fr))',
          mdDown: 'repeat(1, minmax(0, 1fr))',
        },
        gap: '4',
      })}
    >
      <Picker.Root
        inline
        value={color}
        onValueChange={(details) => setColor(details.value)}
        className={paperStyle}
      >
        <Picker.Content
          className={css({ boxShadow: 'none', bg: 'transparent', p: '0', w: 'full' })}
        >
          <Stack gap='4'>
            <Picker.Area h='64' w='full'>
              <Picker.AreaBackground />
              <Picker.AreaThumb />
            </Picker.Area>
            <div className={css({ display: 'flex', alignItems: 'center', gap: '3' })}>
              <Picker.EyeDropperTrigger asChild>
                <IconButton
                  variant='outline'
                  colorPalette='gray'
                  aria-label='Pick a color from the screen'
                >
                  <Pipette />
                </IconButton>
              </Picker.EyeDropperTrigger>
              <Stack gap='2' flex='1'>
                <Picker.ChannelSlider channel='hue'>
                  <Picker.ChannelSliderTrack />
                  <Picker.ChannelSliderThumb />
                </Picker.ChannelSlider>
                <Picker.ChannelSlider channel='alpha'>
                  <Picker.TransparencyGrid size='8px' />
                  <Picker.ChannelSliderTrack />
                  <Picker.ChannelSliderThumb />
                </Picker.ChannelSlider>
              </Stack>
            </div>
            <div
              className={css({
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                gap: '2',
              })}
            >
              <Picker.ChannelInput
                channel='hex'
                className={cx(input({ size: 'sm' }), css({ gridColumn: 'span 4' }))}
              />
              <Picker.ChannelInput
                channel='red'
                aria-label='red'
                className={input({ size: 'sm' })}
              />
              <Picker.ChannelInput
                channel='green'
                aria-label='green'
                className={input({ size: 'sm' })}
              />
              <Picker.ChannelInput
                channel='blue'
                aria-label='blue'
                className={input({ size: 'sm' })}
              />
              <Picker.ChannelInput
                channel='alpha'
                aria-label='alpha'
                className={input({ size: 'sm' })}
              />
            </div>
            <Picker.SwatchGroup className={css({ display: 'flex', flexWrap: 'wrap', gap: '2' })}>
              {SWATCHES.map((swatch) => (
                <Picker.SwatchTrigger key={swatch} value={swatch}>
                  <Picker.Swatch value={swatch} />
                </Picker.SwatchTrigger>
              ))}
            </Picker.SwatchGroup>
          </Stack>
        </Picker.Content>
        <Picker.HiddenInput />
      </Picker.Root>

      <div className={paperStyle}>
        <div className={checkerboard}>
          <div
            className={css({ h: '48', borderRadius: 'l2', transition: 'background-color 0.1s' })}
            style={{ backgroundColor: rgba }}
          />
        </div>
        <Stack gap='4' mt='6' alignItems='flex-start'>
          <Copiable text={hex}>
            <Button size='lg'>
              <Copy />
              {hex}
            </Button>
          </Copiable>
          <Copiable text={rgba}>
            <Button size='lg'>
              <Copy />
              {rgba}
            </Button>
          </Copiable>
        </Stack>
      </div>
    </div>
  );
}
